<template>
  <header
    class="fixed inset-x-0 top-0 z-[1100] border-b border-gray-border/80 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 lg:hidden"
  >
    <div
      class="mx-auto flex h-[var(--layout-mobile-header-height)] w-full max-w-[var(--layout-mobile-max-width)] items-center gap-2 px-[var(--page-gutter)] md:gap-3"
    >
      <NuxtLink
        to="/"
        aria-label="Перейти на главную"
        class="flex shrink-0 items-center gap-2"
      >
        <img
          src="/login/logo-mark.webp"
          alt="АгроПромШина"
          class="h-8 w-8 shrink-0 object-contain md:h-9 md:w-9"
        />
      </NuxtLink>

      <form
        v-if="showSearch"
        class="relative ml-auto flex min-w-0 flex-1 items-center gap-2 rounded-[16px] border border-gray-border bg-white-gray px-3 py-2 md:rounded-[18px] md:px-4 md:py-2.5"
        @submit.prevent="submitSearch"
      >
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          placeholder="Поиск товара..."
          class="min-w-0 flex-1 bg-transparent text-[14px] font-helvetica-400 text-gray-dark placeholder:text-extra-text-color md:text-[15px]"
          @focus="searchHistoryOpen = true"
          @blur="searchHistoryOpen = false"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-dark transition-colors active:bg-white active:text-orange md:h-9 md:w-9"
          aria-label="Очистить поиск"
          @mousedown.prevent
          @click="clearSearchQuery"
        >
          <span
            class="absolute h-[1.75px] w-4 rotate-45 rounded-full bg-current"
          ></span>
          <span
            class="absolute h-[1.75px] w-4 -rotate-45 rounded-full bg-current"
          ></span>
        </button>
        <button
          v-else
          type="submit"
          aria-label="Искать"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-dark transition-colors active:text-orange md:h-9 md:w-9"
        >
          <img
            src="/finder.svg"
            alt=""
            aria-hidden="true"
            class="h-4 w-4 md:h-[18px] md:w-[18px]"
          />
        </button>
        <SearchHistoryDropdown
          :items="searchHistory"
          :open="searchHistoryOpen"
          @select="selectSearchHistory"
        />
      </form>

      <div
        v-if="showSearch && canViewGoods"
        class="flex shrink-0 items-center gap-1.5 md:gap-2"
      >
        <NuxtLink
          to="/favorites"
          aria-label="Избранное"
          class="relative flex h-10 w-10 items-center justify-center rounded-[10px] border border-gray-border bg-white text-gray-dark transition-colors active:border-orange active:text-orange md:h-11 md:w-11"
          :class="
            isFavoritesActive
              ? 'border-orange/20 bg-selected-filter text-orange'
              : ''
          "
        >
          <SvgoIconComponentHIconStar width="22" height="22"></SvgoIconComponentHIconStar>
        </NuxtLink>

        <NuxtLink
          to="/cart"
          aria-label="Корзина"
          class="relative flex h-10 w-10 items-center justify-center rounded-[10px] border border-gray-border bg-white text-gray-dark transition-colors active:border-orange active:text-orange md:h-11 md:w-11"
          :class="
            isCartActive
              ? 'border-orange/20 bg-selected-filter text-orange'
              : ''
          "
        >
          <SvgoIconComponentHIconCart width="24" height="23" fill="currentColor"></SvgoIconComponentHIconCart>
          <span v-if="cartCountNumber > 0" :class="cartBadgeClass">
            {{ cartBadgeLabel }}
          </span>
        </NuxtLink>
      </div>

      <button
        v-else-if="headerAction"
        type="button"
        class="ml-auto flex h-10 items-center rounded-full border border-gray-border bg-white px-4 text-[14px] font-helvetica-400 text-extra-text-color transition-colors active:border-orange active:text-orange md:h-11 md:px-5 md:text-[15px]"
        @click="handleAction"
      >
        <span>{{ headerAction.label }}</span>
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "#imports";
import { useLoginResponse } from "~/composables/useLoginResponse";
import { useSearchHistory } from "~/composables/useSearchHistory";
import SearchHistoryDropdown from "~/components/search/SearchHistoryDropdown.vue";
import { useCartStore } from "~/stores/cartStore";

const route = useRoute();
const router = useRouter();
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const searchHistoryOpen = ref(false);
const cartStore = useCartStore();
const { hydrateLoginResponse, canViewGoods } = useLoginResponse();
const {
  history: searchHistory,
  hydrateSearchHistory,
  addSearchQuery,
} = useSearchHistory();

const normalizedPath = computed(() => route.path.replace(/\/+$/, "") || "/");
const isFavoritesActive = computed(() =>
  normalizedPath.value.startsWith("/favorites"),
);
const isCartActive = computed(() => normalizedPath.value.startsWith("/cart"));
const cartCountNumber = computed(() => cartStore.cartCountNumber);
const cartBadgeLabel = computed(() => {
  if (cartCountNumber.value > 99) return "99+";
  return String(cartCountNumber.value);
});
const cartBadgeClass = computed(() => {
  const isLarge = cartCountNumber.value > 99;
  const isWide = cartCountNumber.value > 9;

  return [
    "absolute -right-[6px] -top-[6px] inline-flex h-[18px] items-center justify-center rounded-full bg-orange font-helvetica-700 text-white tabular-nums shadow-[0_1px_3px_rgba(17,17,20,0.18)] ring-2 ring-white md:-right-[7px] md:-top-[7px] md:h-[19px]",
    isLarge
      ? "min-w-[28px] px-[6px] text-[12px] leading-none"
      : isWide
        ? "min-w-[23px] px-[5px] text-[12px] leading-none"
        : "min-w-[18px] px-1 text-[12px] leading-none",
  ].join(" ");
});
const routeSearchQuery = computed(() => {
  const query =
    route.query.q ?? route.query.search ?? route.query.search_string;
  return typeof query === "string" ? query : "";
});
const showSearch = computed(
  () =>
    !normalizedPath.value.startsWith("/login") &&
    !normalizedPath.value.startsWith("/register") &&
    !normalizedPath.value.startsWith("/info"),
);

const headerAction = computed(() => {
  const path = normalizedPath.value;

  if (
    !showSearch.value &&
    (path.startsWith("/goods/") || path.startsWith("/chat"))
  ) {
    return {
      label: "Назад",
      type: "back" as const,
    };
  }

  if (
    !showSearch.value &&
    (path.startsWith("/search/") || path === "/favorites" || path === "/cart")
  ) {
    return {
      label: "Подбор",
      type: "link" as const,
      to: "/search",
    };
  }

  return null;
});

const handleAction = () => {
  if (!headerAction.value) return;

  if (headerAction.value.type === "back") {
    if (window.history.length > 1) {
      router.back();
      return;
    }
    router.push("/search");
    return;
  }

  router.push(headerAction.value.to);
};

const submitSearch = () => {
  const query = searchQuery.value.trim();

  if (!query) {
    router.push("/search");
    return;
  }

  addSearchQuery(query);
  searchHistoryOpen.value = false;
  router.push({ path: "/search/any", query: { q: query } });
};

const clearSearchQuery = () => {
  searchQuery.value = "";
  searchHistoryOpen.value = true;
  void nextTick(() => searchInput.value?.focus());
};

const selectSearchHistory = (value: string) => {
  searchQuery.value = value;
  addSearchQuery(value);
  searchHistoryOpen.value = false;
  router.push({ path: "/search/any", query: { q: value } });
};

watch(
  routeSearchQuery,
  (value) => {
    searchQuery.value = value;
  },
  { immediate: true },
);

if (process.client) {
  hydrateLoginResponse();
  hydrateSearchHistory();
}
</script>
